import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

interface UpdateUserRoleParams {
  userId: string;
  companyId: string;
  newRole: string; // 'owner', 'admin', 'coach', 'reception', 'student'
}

/**
 * Hook to update a user's role in a company
 * Admin can update any user's role in any company
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, companyId, newRole }: UpdateUserRoleParams) => {
      // First, lookup the role_id for the role name
      const { data: roleData, error: roleError } = await supabase
        .from('system_roles')
        .select('system_role_id, role')
        .eq('role', newRole)
        .single();

      if (roleError || !roleData) {
        throw new Error(`Role lookup failed: ${roleError?.message || 'Unknown error'}`);
      }

      // Update the user_company_relationship
      const { data, error } = await supabase
        .from('user_company_relationships')
        .update({ role_id: roleData.system_role_id })
        .eq('user_id', userId)
        .eq('company_id', companyId)
        .select('id, role_id')
        .single();

      if (error) {
        throw new Error(`Failed to update role: ${error.message}`);
      }

      return { ...data, role: newRole };
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['admin', 'company-users', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['companyUsers', variables.companyId] });
      queryClient.invalidateQueries({ queryKey: ['companyUsers'] }); // Generic fallback
    },
  });
}
